import { Controller } from "@hotwired/stimulus"
import { loadMapLibre, loadBaseStyle, createPin, escapeHtml } from "../lib/maplibre"

// A guide's map: a pin for each of its picks in its event type's colour, and each of its walks drawn as
// a route with a lettered pin at its start. Tapping a walk (on the map or in the list below it) zooms
// to it. The map only loads once its tab is open, since a hidden map has no size to draw at.
const FIT_PADDING = { top: 70, bottom: 30, left: 30, right: 30 }
const WALK_PADDING = { top: 80, bottom: 40, left: 40, right: 40 }
const MAX_FIT_ZOOM = 16

export default class extends Controller {
  static targets = ["container"]
  static values = { data: Object }

  connect() {
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        this.observer.disconnect()
        this.load()
      }
    })
    this.observer.observe(this.containerTarget)
  }

  disconnect() {
    this.observer?.disconnect()
    this.generation = (this.generation || 0) + 1
    this.map?.remove()
    this.map = null
  }

  async load() {
    const generation = this.generation = (this.generation || 0) + 1
    const { pins = [], walks = [] } = this.dataValue
    const points = pins.map(pin => [pin.lng, pin.lat])
    walks.forEach(walk => points.push(...walkPoints(walk)))
    if (points.length === 0) return

    let maplibregl, style
    try {
      [maplibregl, style] = await Promise.all([loadMapLibre(), loadBaseStyle()])
    } catch (error) {
      console.warn("Map unavailable:", error)
      return
    }
    if (generation !== this.generation) return

    this.maplibregl = maplibregl
    // Turbo's cached page snapshots can include the previous map
    this.containerTarget.replaceChildren()
    try {
      this.map = new maplibregl.Map({
        container: this.containerTarget,
        style,
        bounds: bounds(points),
        fitBoundsOptions: { padding: FIT_PADDING, maxZoom: MAX_FIT_ZOOM },
        attributionControl: { compact: true },
        // One finger scrolls the page on phones; two fingers (or ctrl + scroll) move the map
        cooperativeGestures: true,
        dragRotate: false,
        pitchWithRotate: false,
        touchPitch: false
      })
    } catch (error) {
      console.warn("Map unavailable:", error)
      return
    }
    this.map.touchZoomRotate.disableRotation()
    this.map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right")
    this.map.on("load", () => this.drawWalks(walks))

    pins.forEach(pin => {
      const element = createPin({ color: pin.color, icon: pin.icon, label: pin.title })
      const popup = new maplibregl.Popup({ offset: 28, closeButton: false, maxWidth: "240px" }).setHTML(
        `<a href="${escapeHtml(pin.url)}" class="block text-footnote font-semibold text-stone-900">${escapeHtml(pin.title)}</a>` +
        (pin.venue ? `<span class="block text-caption text-stone-500">${escapeHtml(pin.venue)}</span>` : "")
      )
      new maplibregl.Marker({ element, anchor: "bottom" }).setLngLat([pin.lng, pin.lat]).setPopup(popup).addTo(this.map)
    })
  }

  drawWalks(walks) {
    walks.forEach((walk, index) => {
      const legs = walk.route.legs.filter(leg => leg.mode === "walk" && leg.coordinates.length > 1)
      const id = `guide-walk-${index}`
      this.map.addSource(id, {
        type: "geojson",
        data: { type: "FeatureCollection", features: legs.map(leg => ({ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: leg.coordinates } })) }
      })
      this.map.addLayer({ id: `${id}-casing`, type: "line", source: id, layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#ffffff", "line-width": 9 } })
      this.map.addLayer({ id, type: "line", source: id, layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#1c1917", "line-width": 5 } })
      this.map.on("click", id, () => this.zoomTo(walk))
      this.map.on("mouseenter", id, () => { this.map.getCanvas().style.cursor = "pointer" })
      this.map.on("mouseleave", id, () => { this.map.getCanvas().style.cursor = "" })

      const start = walk.route.pins[0]
      if (!start) return
      const element = document.createElement("button")
      element.type = "button"
      element.className = "walk-pin cursor-pointer"
      element.textContent = walk.label
      element.setAttribute("aria-label", `Walk ${walk.label}: ${walk.title}`)
      element.addEventListener("click", (event) => {
        event.stopPropagation()
        this.zoomTo(walk)
      })
      new this.maplibregl.Marker({ element }).setLngLat([start.lng, start.lat]).addTo(this.map)
    })
  }

  focus(event) {
    const walk = this.dataValue.walks?.find(candidate => candidate.token === event.currentTarget.dataset.token)
    if (!walk) return

    this.containerTarget.scrollIntoView({ block: "center", behavior: "smooth" })
    this.zoomTo(walk)
  }

  zoomTo(walk) {
    if (!this.map) return
    this.map.fitBounds(bounds(walkPoints(walk)), { padding: WALK_PADDING, maxZoom: MAX_FIT_ZOOM, duration: 700 })
  }
}

function walkPoints(walk) {
  const points = walk.route.pins.map(pin => [pin.lng, pin.lat])
  walk.route.legs.filter(leg => leg.mode === "walk").forEach(leg => points.push(...leg.coordinates))
  return points
}

function bounds(points) {
  const lngs = points.map(([lng]) => lng)
  const lats = points.map(([, lat]) => lat)
  return [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]]
}
